import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Alert, AlertDescription } from './ui/alert';
import { useAuth } from '../contexts/AuthContext';
import { Loader2, CheckCircle, User, Mail, Lock, Hash, Eye, EyeOff, Store, Building, Check, X, MapPin } from 'lucide-react';
import { useEffect } from 'react';

const AuthModal = ({ isOpen, onOpenChange, onLoginSuccess, initialMode = 'login' }) => {
  const [isLogin, setIsLogin] = useState(initialMode === 'login');
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [isResetPassword, setIsResetPassword] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [stallNumber, setStallNumber] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [pendingMessage, setPendingMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [vendorType, setVendorType] = useState('');
  const [mainLocation, setMainLocation] = useState('');
  const [locationOptions, setLocationOptions] = useState([]);

  // Update isLogin when initialMode changes
  useEffect(() => {
    if (isOpen) {
      setIsLogin(initialMode === 'login');
      setIsForgotPassword(false);
      setIsResetPassword(false);
    }
  }, [isOpen, initialMode]);

  const { login, register } = useAuth();
  const API_URL = window.location.origin;

  // Fetch location options when vendor type changes
  useEffect(() => {
    if (!vendorType) {
      setLocationOptions([]);
      setMainLocation('');
      return;
    }
    const endpoint = vendorType === 'market' ? 'markets' : vendorType === 'expo' ? 'expos' : 'festivals';
    fetch(`${API_URL}/api/categories/${endpoint}`)
      .then(res => res.json())
      .then(data => {
        setLocationOptions(Array.isArray(data) ? data : []);
        setMainLocation('');
      })
      .catch(() => setLocationOptions([]));
  }, [vendorType, API_URL]);

  // Password strength validation
  const passwordChecks = {
    minLength: password.length >= 10,
    hasUppercase: /[A-Z]/.test(password),
    hasNumber: /[0-9]/.test(password),
    hasSpecial: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password),
  };

  const isPasswordStrong = Object.values(passwordChecks).every(Boolean);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setPendingMessage('');
    setLoading(true);

    try {
      if (isLogin) {
        const result = await login(email, password);
        if (result.success) {
          onOpenChange(false);
          resetForm();
          // Open dashboard immediately after successful login
          if (onLoginSuccess) {
            onLoginSuccess();
          }
        } else {
          setError(result.error);
        }
      } else {
        // Validate password strength
        if (!isPasswordStrong) {
          setError('Password does not meet the requirements');
          setLoading(false);
          return;
        }
        // Validate passwords match
        if (password !== confirmPassword) {
          setError('Passwords do not match');
          setLoading(false);
          return;
        }
        // Validate vendor type selected
        if (!vendorType) {
          setError('Please select a vendor type');
          setLoading(false);
          return;
        }
        // Validate main location selected
        if (!mainLocation) {
          setError('Please select a main location');
          setLoading(false);
          return;
        }
        const result = await register(name, email, password, 'vendor', phone, stallNumber, firstName, lastName, vendorType, mainLocation);
        if (result.success) {
          if (result.pending) {
            setPendingMessage(result.message);
            // Don't auto-close - let user close the modal manually
          } else {
            onOpenChange(false);
            resetForm();
          }
        } else {
          setError(result.error);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFirstName('');
    setLastName('');
    setName('');
    setPhone('');
    setStallNumber('');
    setEmail('');
    setPassword('');
    setConfirmPassword('');
    setNewPassword('');
    setConfirmNewPassword('');
    setResetToken('');
    setError('');
    setPendingMessage('');
    setSuccessMessage('');
    setVendorType('');
    setMainLocation('');
    setLocationOptions([]);
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      if (!response.ok) {
        // Try to parse error message
        try {
          const data = await response.json();
          if (response.status === 404 || data.detail?.toLowerCase().includes('not found')) {
            throw new Error('The email address was not found. Please verify and try again.');
          }
          throw new Error(data.detail || 'Failed to send reset link');
        } catch (parseError) {
          if (parseError.message.includes('email address was not found')) {
            throw parseError;
          }
          throw new Error('The email address was not found. Please verify and try again.');
        }
      }

      const data = await response.json();
      setSuccessMessage('A password reset link has been sent to your email. Please check your inbox and follow the instructions.');
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');

    if (newPassword !== confirmNewPassword) {
      setError('Passwords do not match');
      return;
    }

    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token: resetToken,
          new_password: newPassword,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to reset password');
      }

      setSuccessMessage(data.message);
      setTimeout(() => {
        setIsResetPassword(false);
        setIsLogin(true);
        resetForm();
      }, 2000);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const toggleMode = () => {
    setIsLogin(!isLogin);
    setIsForgotPassword(false);
    setIsResetPassword(false);
    resetForm();
  };

  const showForgotPassword = () => {
    setIsForgotPassword(true);
    setIsLogin(false);
    setIsResetPassword(false);
    resetForm();
  };

  const backToLogin = () => {
    setIsForgotPassword(false);
    setIsResetPassword(false);
    setIsLogin(true);
    resetForm();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md bg-white dark:bg-[#1a1a1a] border-4 border-[#FFD12D] rounded-2xl shadow-2xl">
        <DialogHeader className="border-b-2 border-[#FFD12D] pb-4">
          {isForgotPassword ? (
            <>
              <DialogTitle className="text-2xl font-bold text-[#252525] dark:text-white flex items-center gap-2">
                <div className="w-10 h-10 rounded-full bg-[#FFD12D] flex items-center justify-center">
                  <User className="w-5 h-5 text-[#252525]" />
                </div>
                Forgot Password
              </DialogTitle>
              <DialogDescription className="text-gray-600 dark:text-gray-400">
                Please enter your email address linked to your account to reset your password.
              </DialogDescription>
            </>
          ) : (
            <>
              <DialogTitle className="text-2xl font-bold text-[#252525] dark:text-white flex items-center gap-2">
                <div className="w-10 h-10 rounded-full bg-[#FFD12D] flex items-center justify-center">
                  <User className="w-5 h-5 text-[#252525]" />
                </div>
                {isResetPassword ? 'Reset Password' : isLogin ? 'Log In' : 'Sign Up'}
              </DialogTitle>
              <DialogDescription className="text-gray-600 dark:text-gray-400">
                {isResetPassword
                  ? 'Enter your new password'
                  : isLogin
                  ? 'Enter your credentials to access your account'
                  : 'Create a vendor account to manage your stall'}
              </DialogDescription>
            </>
          )}
        </DialogHeader>

        {pendingMessage && (
          <Alert className="bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800">
            <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />
            <AlertDescription className="text-green-800 dark:text-green-200">
              {pendingMessage}
            </AlertDescription>
          </Alert>
        )}

        {successMessage && (
          <Alert className="bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800">
            <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />
            <AlertDescription className="text-green-800 dark:text-green-200">
              {successMessage}
            </AlertDescription>
          </Alert>
        )}

        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Forgot Password Form */}
        {isForgotPassword && (
          <form onSubmit={handleForgotPassword} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-[#252525] dark:text-white flex items-center gap-2">
                <Mail className="w-4 h-4 text-[#FFD12D]" />
                Email
              </Label>
              <Input
                id="email"
                type="email"
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="border-2 border-gray-300 dark:border-gray-600 dark:bg-[#252525] dark:text-white focus:border-[#FFD12D] focus:ring-2 focus:ring-[#FFD12D]/20 placeholder:text-gray-400"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#FFD12D] hover:bg-[#252525] text-[#252525] hover:text-[#FFD12D] font-semibold py-6 transition-all duration-300 shadow-lg"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  Sending...
                </>
              ) : (
                'Send Reset Link'
              )}
            </Button>

            <div className="text-center">
              <button
                type="button"
                onClick={backToLogin}
                className="text-[#FFD12D] hover:text-[#252525] dark:hover:text-white font-semibold transition-colors"
              >
                Back to Log In
              </button>
            </div>
          </form>
        )}

        {/* Reset Password Form */}
        {isResetPassword && (
          <form onSubmit={handleResetPassword} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="newPassword" className="text-[#252525] dark:text-white flex items-center gap-2">
                <Lock className="w-4 h-4 text-[#FFD12D]" />
                New Password
              </Label>
              <div className="relative">
                <Input
                  id="newPassword"
                  type={showNewPassword ? 'text' : 'password'}
                  placeholder="Enter new password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  className="border-2 border-gray-300 dark:border-gray-600 dark:bg-[#252525] dark:text-white focus:border-[#FFD12D] focus:ring-2 focus:ring-[#FFD12D]/20 pr-10 placeholder:text-gray-400"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-[#FFD12D]"
                >
                  {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmNewPassword" className="text-[#252525] dark:text-white flex items-center gap-2">
                <Lock className="w-4 h-4 text-[#FFD12D]" />
                Confirm New Password
              </Label>
              <Input
                id="confirmNewPassword"
                type={showNewPassword ? 'text' : 'password'}
                placeholder="Confirm new password"
                value={confirmNewPassword}
                onChange={(e) => setConfirmNewPassword(e.target.value)}
                required
                className="border-2 border-gray-300 dark:border-gray-600 dark:bg-[#252525] dark:text-white focus:border-[#FFD12D] focus:ring-2 focus:ring-[#FFD12D]/20 placeholder:text-gray-400"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#FFD12D] hover:bg-[#252525] text-[#252525] hover:text-[#FFD12D] font-semibold py-6 transition-all duration-300 shadow-lg"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  Resetting...
                </>
              ) : (
                'Reset Password'
              )}
            </Button>
          </form>
        )}

        {/* Login/Signup Form */}
        {!isForgotPassword && !isResetPassword && (
        <form onSubmit={handleSubmit} className="space-y-4">
          {!isLogin && (
            <>
              {/* Sign up as dropdown */}
              <div className="space-y-2">
                <Label htmlFor="vendorType" className="text-[#252525] dark:text-white flex items-center gap-2">
                  <Store className="w-4 h-4 text-[#FFD12D]" />
                  Sign up as
                </Label>
                <select
                  id="vendorType"
                  value={vendorType}
                  onChange={(e) => setVendorType(e.target.value)}
                  required
                  data-testid="signup-vendor-type"
                  className="w-full h-10 px-3 rounded-md border-2 border-gray-300 dark:border-gray-600 dark:bg-[#252525] dark:text-white focus:border-[#FFD12D] focus:ring-2 focus:ring-[#FFD12D]/20 text-sm"
                >
                  <option value="">Choose option</option>
                  <option value="market">Market Vendor</option>
                  <option value="expo">Expo Vendor</option>
                  <option value="festival">Festival Vendor</option>
                </select>
              </div>

              {/* Main Location dropdown — filtered by vendor type */}
              {vendorType && (
                <div className="space-y-2">
                  <Label htmlFor="mainLocation" className="text-[#252525] dark:text-white flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-[#FFD12D]" />
                    Main Location
                  </Label>
                  <select
                    id="mainLocation"
                    value={mainLocation}
                    onChange={(e) => setMainLocation(e.target.value)}
                    required
                    data-testid="signup-main-location"
                    className="w-full h-10 px-3 rounded-md border-2 border-gray-300 dark:border-gray-600 dark:bg-[#252525] dark:text-white focus:border-[#FFD12D] focus:ring-2 focus:ring-[#FFD12D]/20 text-sm"
                  >
                    <option value="">Select {vendorType === 'market' ? 'market' : vendorType === 'expo' ? 'expo' : 'festival'}</option>
                    {locationOptions.map((loc) => (
                      <option key={loc} value={loc}>{loc}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Row 1: Stall Number | Company Name */}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="stallNumber" className="text-[#252525] dark:text-white flex items-center gap-2">
                    <Hash className="w-4 h-4 text-[#FFD12D]" />
                    Stall Number
                  </Label>
                  <Input
                    id="stallNumber"
                    type="text"
                    placeholder="e.g., A-15"
                    value={stallNumber}
                    onChange={(e) => setStallNumber(e.target.value)}
                    required
                    data-testid="signup-stall-number"
                    className="border-2 border-gray-300 dark:border-gray-600 dark:bg-[#252525] dark:text-white focus:border-[#FFD12D] focus:ring-2 focus:ring-[#FFD12D]/20 placeholder:text-gray-400"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-[#252525] dark:text-white flex items-center gap-2">
                    <Building className="w-4 h-4 text-[#FFD12D]" />
                    Company Name
                  </Label>
                  <Input
                    id="name"
                    type="text"
                    placeholder="Your company name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    data-testid="signup-company-name"
                    className="border-2 border-gray-300 dark:border-gray-600 dark:bg-[#252525] dark:text-white focus:border-[#FFD12D] focus:ring-2 focus:ring-[#FFD12D]/20 placeholder:text-gray-400"
                  />
                </div>
              </div>

              {/* Row 2: Name | Surname */}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="firstName" className="text-[#252525] dark:text-white flex items-center gap-2">
                    <User className="w-4 h-4 text-[#FFD12D]" />
                    Name
                  </Label>
                  <Input
                    id="firstName"
                    type="text"
                    placeholder="John"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    required
                    data-testid="signup-first-name"
                    className="border-2 border-gray-300 dark:border-gray-600 dark:bg-[#252525] dark:text-white focus:border-[#FFD12D] focus:ring-2 focus:ring-[#FFD12D]/20 placeholder:text-gray-400"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lastName" className="text-[#252525] dark:text-white flex items-center gap-2">
                    <User className="w-4 h-4 text-[#FFD12D]" />
                    Surname
                  </Label>
                  <Input
                    id="lastName"
                    type="text"
                    placeholder="Doe"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    required
                    data-testid="signup-last-name"
                    className="border-2 border-gray-300 dark:border-gray-600 dark:bg-[#252525] dark:text-white focus:border-[#FFD12D] focus:ring-2 focus:ring-[#FFD12D]/20 placeholder:text-gray-400"
                  />
                </div>
              </div>

              {/* Row 3: Phone Number | Email */}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-[#252525] dark:text-white flex items-center gap-2">
                    <svg className="w-4 h-4 text-[#FFD12D]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                    </svg>
                    Phone Number
                  </Label>
                  <Input
                    id="phone"
                    type="tel"
                    placeholder="+27 74 809 4378"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    required
                    data-testid="signup-phone"
                    className="border-2 border-gray-300 dark:border-gray-600 dark:bg-[#252525] dark:text-white focus:border-[#FFD12D] focus:ring-2 focus:ring-[#FFD12D]/20 placeholder:text-gray-400"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-[#252525] dark:text-white flex items-center gap-2">
                    <Mail className="w-4 h-4 text-[#FFD12D]" />
                    Email
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="your@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="border-2 border-gray-300 dark:border-gray-600 dark:bg-[#252525] dark:text-white focus:border-[#FFD12D] focus:ring-2 focus:ring-[#FFD12D]/20 placeholder:text-gray-400"
                  />
                </div>
              </div>

              {/* Row 4: Password | Repeat Password */}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-[#252525] dark:text-white flex items-center gap-2">
                    <Lock className="w-4 h-4 text-[#FFD12D]" />
                    Password
                  </Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      className="border-2 border-gray-300 dark:border-gray-600 dark:bg-[#252525] dark:text-white focus:border-[#FFD12D] focus:ring-2 focus:ring-[#FFD12D]/20 placeholder:text-gray-400 pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-[#FFD12D]"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword" className="text-[#252525] dark:text-white flex items-center gap-2">
                    <Lock className="w-4 h-4 text-[#FFD12D]" />
                    Repeat Password
                  </Label>
                  <div className="relative">
                    <Input
                      id="confirmPassword"
                      type={showConfirmPassword ? "text" : "password"}
                      placeholder="••••••••"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      className="border-2 border-gray-300 dark:border-gray-600 dark:bg-[#252525] dark:text-white focus:border-[#FFD12D] focus:ring-2 focus:ring-[#FFD12D]/20 placeholder:text-gray-400 pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-[#FFD12D]"
                    >
                      {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
              </div>

              {/* Password Strength Indicator */}
              {password && (
                <div className="bg-gray-50 dark:bg-[#252525] p-3 rounded-lg border border-gray-200 dark:border-gray-700">
                  <p className="text-xs font-semibold text-[#252525] dark:text-white mb-2">Password Requirements:</p>
                  <div className="grid grid-cols-2 gap-1">
                    <div className={`flex items-center gap-1 text-xs ${passwordChecks.minLength ? 'text-green-600' : 'text-gray-400'}`}>
                      {passwordChecks.minLength ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                      Minimum 10 characters
                    </div>
                    <div className={`flex items-center gap-1 text-xs ${passwordChecks.hasUppercase ? 'text-green-600' : 'text-gray-400'}`}>
                      {passwordChecks.hasUppercase ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                      One uppercase letter
                    </div>
                    <div className={`flex items-center gap-1 text-xs ${passwordChecks.hasNumber ? 'text-green-600' : 'text-gray-400'}`}>
                      {passwordChecks.hasNumber ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                      One number
                    </div>
                    <div className={`flex items-center gap-1 text-xs ${passwordChecks.hasSpecial ? 'text-green-600' : 'text-gray-400'}`}>
                      {passwordChecks.hasSpecial ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                      One special character
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Login-only fields */}
          {isLogin && (
            <>
              <div className="space-y-2">
                <Label htmlFor="email" className="text-[#252525] dark:text-white flex items-center gap-2">
                  <Mail className="w-4 h-4 text-[#FFD12D]" />
                  Email
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="border-2 border-gray-300 dark:border-gray-600 dark:bg-[#252525] dark:text-white focus:border-[#FFD12D] focus:ring-2 focus:ring-[#FFD12D]/20 placeholder:text-gray-400"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-[#252525] dark:text-white flex items-center gap-2">
                  <Lock className="w-4 h-4 text-[#FFD12D]" />
                  Password
                </Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="border-2 border-gray-300 dark:border-gray-600 dark:bg-[#252525] dark:text-white focus:border-[#FFD12D] focus:ring-2 focus:ring-[#FFD12D]/20 placeholder:text-gray-400 pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-[#FFD12D]"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <div className="text-right">
                  <button
                    type="button"
                    onClick={showForgotPassword}
                    className="text-sm text-[#FFD12D] hover:text-[#252525] dark:hover:text-white font-semibold transition-colors"
                  >
                    Forgot Password?
                  </button>
                </div>
              </div>
            </>
          )}

          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-[#FFD12D] hover:bg-[#252525] text-[#252525] hover:text-[#FFD12D] font-semibold py-6 transition-all duration-300 shadow-lg transform hover:scale-105 border-2 border-[#FFD12D]"
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                {isLogin ? 'Logging in...' : 'Creating account...'}
              </>
            ) : (
              <>{isLogin ? 'Log In' : 'Sign Up'}</>
            )}
          </Button>
        </form>
        )}

        {!isForgotPassword && !isResetPassword && (
        <div className="text-center text-sm border-t-2 border-[#FFD12D] pt-4">
          <button
            type="button"
            onClick={toggleMode}
            className="text-[#252525] dark:text-[#FFD12D] hover:underline font-semibold"
          >
            {isLogin
              ? "Don't have an account? Sign up"
              : 'Already have an account? Log in'}
          </button>
        </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AuthModal;
